import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { addDaysIso, dayLabel, formatPtDate } from "@/lib/deliveryDate";

interface Props {
  value: string;
  today: string;
  onChange: (next: string) => void;
}

function parseIso(iso: string): Date | undefined {
  const [y, m, d] = (iso || "").split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function formatIso(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function DateNavigator({ value, today, onChange }: Props) {
  const isToday = value === today;
  const [open, setOpen] = useState(false);
  const selected = parseIso(value);

  return (
    <div className="flex items-stretch gap-2">
      <div className="flex flex-1 items-center overflow-hidden rounded-lg bg-muted/40">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0 rounded-r-none text-muted-foreground hover:bg-muted"
          onClick={() => onChange(addDaysIso(value, -1))}
          aria-label="Dia anterior"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Selecionar data"
              className="flex h-10 flex-1 items-center justify-center gap-2 px-2 text-sm font-medium hover:bg-muted/70 active:bg-muted"
            >
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
              <span className="first-letter:uppercase">{dayLabel(value, today)}</span>
              <span className="text-muted-foreground">·</span>
              <span className="tabular-nums text-muted-foreground">{formatPtDate(value)}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="center">
            <Calendar
              mode="single"
              selected={selected}
              defaultMonth={selected}
              onSelect={(d) => {
                if (!d) return;
                onChange(formatIso(d));
                setOpen(false);
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0 rounded-l-none text-muted-foreground hover:bg-muted"
          onClick={() => onChange(addDaysIso(value, 1))}
          aria-label="Próximo dia"
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>
      <Button
        type="button"
        variant={isToday ? "default" : "outline"}
        onClick={() => onChange(today)}
        disabled={isToday}
        className="h-10 shrink-0 px-4"
      >
        Hoje
      </Button>
    </div>
  );
}
